/**
 * Pure decision logic for the VTT canvas mouse-input model.
 *
 * The wiring that fires these (timers, PIXI hit-tests, socket emits) lives in
 * the large VTTCanvas component, but the *decisions* — "is this left press a
 * ping, a marquee, or a click?" and "should this button start a pan?" — are
 * extracted here so they can be unit-tested without a DOM or a PIXI stage.
 *
 * Input model (product spec):
 *   - LEFT press on EMPTY canvas, HELD past a threshold with minimal movement
 *     → place a ping. Moved first → marquee (rubber-band select). Released
 *     before the threshold → a normal click.
 *   - LEFT press on a TOKEN → never a ping (existing token-drag / select path).
 *   - RIGHT (button 2) or MIDDLE (button 1) drag on EMPTY canvas → pan.
 *   - RIGHT press on a TOKEN → no pan (reserved for the tooltip / future menu).
 */

/** Pointer button codes (PointerEvent.button). */
export const MOUSE_BUTTON = {
  LEFT: 0,
  MIDDLE: 1,
  RIGHT: 2,
} as const

/**
 * How long (ms) a left press must be held — with minimal movement — before it
 * is treated as a ping rather than a click. Long enough not to fire on a quick
 * tap, short enough to feel intentional.
 */
export const PING_LONG_PRESS_MS = 450

/**
 * Movement tolerance (screen px) for a long press. If the pointer travels more
 * than this before the threshold elapses, the gesture is a marquee drag, not a
 * ping. Matches the existing token DRAG_THRESHOLD feel (~5px).
 */
export const PING_MOVE_TOLERANCE_PX = 6

export type LeftPressOutcome = 'ping' | 'marquee' | 'click'

/**
 * Classify a completed/elapsed left-button press on the canvas.
 *
 * @param heldMs   how long the button has been (or was) held, in ms
 * @param movedPx  total pointer travel since press-down, in screen px
 * @param onToken  true if the press landed on a token (vs empty canvas)
 *
 * Resolution order:
 *   1. On a token → 'click' (the token-drag/select path owns it; never a ping).
 *   2. Moved past tolerance → 'marquee' (rubber-band), regardless of duration —
 *      movement is the strongest signal of intent to drag-select.
 *   3. Held past the threshold (and still) → 'ping'.
 *   4. Otherwise → 'click' (quick tap).
 */
export function classifyLeftPress({
  heldMs,
  movedPx,
  onToken,
}: {
  heldMs: number
  movedPx: number
  onToken: boolean
}): LeftPressOutcome {
  if (onToken) return 'click'
  if (movedPx > PING_MOVE_TOLERANCE_PX) return 'marquee'
  if (heldMs >= PING_LONG_PRESS_MS) return 'ping'
  return 'click'
}

/**
 * Decide whether a pointer-down should begin a map pan.
 *
 * Middle button (1) always pans — unconditional, matching the legacy behavior.
 * Right button (2) pans only on EMPTY canvas; on a token the right press is
 * reserved (tooltip today, context menu later), so it must NOT pan. Left button
 * never pans.
 */
export function shouldPanForButton(button: number, onToken: boolean): boolean {
  if (button === MOUSE_BUTTON.MIDDLE) return true
  if (button === MOUSE_BUTTON.RIGHT) return !onToken
  return false
}

/**
 * Whether a click in a placement tool mode (light, barrier) should DROP an
 * object. Placement is LEFT-button only — middle always pans and right is
 * reserved for pan/context, so neither may place a light. Touch taps report
 * button 0 (LEFT), so touch placement is unaffected. (#554)
 */
export function shouldPlaceForButton(button: number): boolean {
  return button === MOUSE_BUTTON.LEFT
}
